import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import axios from 'axios';
import { ExistingRepoPickerState } from '../../interfaces';

interface RepositoryItem {
  id: string;
  name: string;
  remoteUrl: string;
  defaultBranch?: string;
}

interface RepositoriesResponse {
  count: number;
  value: RepositoryItem[];
}

async function getRepositories(
  config: any,
  project: string,
): Promise<RepositoriesResponse> {
  const baseUrl = config.getString('backend.baseUrl');
  const response = await axios.get(
    `${baseUrl}/api/azure-devops/repositories`,
    { params: { project } },
  );

  return response.data;
}

interface RepositoryPickerProps {
  project?: string;
  repoName?: string;
  onChange: (state: Partial<ExistingRepoPickerState>) => void;
  rawErrors: string[];
}

export default function RepositoryPicker(
  props: Readonly<RepositoryPickerProps>,
): JSX.Element {
  const { project, repoName, onChange, rawErrors } = props;
  const config = useApi(configApiRef);
  const [repositories, setRepositories] = useState<RepositoryItem[]>([]);
  const [currentSelection, setCurrentSelection] = useState<
    string | undefined
  >(repoName);

  useEffect(() => {
    setCurrentSelection(repoName);
  }, [repoName]);

  useEffect(() => {
    if (!project) {
      setRepositories([]);
      return;
    }

    (async () => {
      const res = await getRepositories(config, project);
      setRepositories(res?.value ?? []);
    })();
  }, [project, config]);

  const options = useMemo(() => repositories.map(r => r.name), [repositories]);

  const handleOnChange = useCallback(
    (_e: any, value: any) => {
      const selected = repositories.find(r => r.name === value);
      setCurrentSelection(value ?? undefined);
      onChange({
        repoName: value ?? undefined,
        repositoryId: selected?.id,
        remoteUrl: selected?.remoteUrl,
        defaultBranch: selected?.defaultBranch,
      });
    },
    [repositories, onChange],
  );

  return (
    <FormControl
      margin="normal"
      required
      error={rawErrors?.length > 0 && !repoName}
      disabled={!project}
    >
      <Autocomplete
        getOptionLabel={option => option}
        onChange={handleOnChange}
        options={options}
        value={currentSelection ?? null}
        renderInput={params => (
          <TextField {...params} label="Azure DevOps Repository *" />
        )}
        data-testid="azure-devops-existing-repository-select"
      />

      <FormHelperText>
        Choose the existing Azure DevOps repository to add the Renovate
        pipeline to
      </FormHelperText>
    </FormControl>
  );
}
